import { Avatar, Badge, Box, Collapse, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { v5 as uuidv5 } from 'uuid'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import { getNameInitials, Icons, USER_CARD_COLOR_SETS } from '../../utils/utilities'
import { useSocketsContext } from '../../contexts/sockets.context'
import { useMeetingStore } from '../../store/meeting.store'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'

const NAMESPACE = "6ba7b810-9dad-11d1-80b4-00c04fd430c8"

function pairUUID ( a, b ) {
    const [x, y] = [a, b].sort()
    return uuidv5( `${x}-${y}`, NAMESPACE )
}

const OnlineUsersListCard = observer( ( { user, } ) => {

    const [openOptions, setOpenOptions] = useState( false )

    const { meeting_id } = useParams()
    const { palette } = useTheme()

    // const { emitEvent } = useSocketsContext()
    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()

    const MeetingStore = useMeetingStore()

    const userDetails = useMemo( () => {
        const details = {}
        details.user_id = user.user_id
        details.name = user.name || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.name || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.email || user.user_id
        details.email = user.email || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.email || user.user_id
        details.type = user.type || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.type
        details.meeting_role = user.meeting_role || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.meeting_role
        details.color_index = user.color_index || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.color_index
        details.pinned = user.pinned
        details.alive_user = user.alive_user
        details.reconnecting = !Boolean( user?.name )
        return details
    }, [user, MeetingStore] )

    const userPermission = useMemo( () => {
        const existingPermission = MeetingStore.getUsersPermissions[userDetails.user_id]
        return {
            mic: existingPermission?.mic ?? MeetingStore.getCurrentMeetingSettings.enable_microphone,
            camera: existingPermission?.camera ?? MeetingStore.getCurrentMeetingSettings.enable_video_feed,
            screenSharing: existingPermission?.screenSharing ?? MeetingStore.getCurrentMeetingSettings.enable_screen_sharing
        }
    }, [userDetails, MeetingStore.getCurrentMeetingSettings, MeetingStore.getUsersPermissions] )


    const selectUserForChat = user => {
        const currentRecentChats = [...( MeetingStore.getMeetingChatsDetails?.recentChats || [] )]
        const userChatDetails = currentRecentChats.find( u => u.user_id === user.user_id )
        let chatId = userChatDetails?.chat_id
        if ( !userChatDetails ) {
            chatId = pairUUID( user.user_id, MeetingStore.getLocalUser.user_id )
            currentRecentChats.push( { ...user, chat_id: chatId } )
        }
        MeetingStore.setMeetingChatsDetails( { recentChats: currentRecentChats, currentChatId: chatId, currentChatRecipent: { name: user.name, user_id: user.user_id, color_index: user.color_index } } )
        MeetingStore.setMeetingRoomLayoutSettings( { leftSideCollapseTab: 'chats', isLeftSideCollapseOpen: true } )
    }

    const togglePinUser = ( userId ) => {
        MeetingStore.togglePinUser( userId )
    }

    const updateUserPermissions = async ( permission ) => {
        const newValue = { ...userPermission }
        newValue[permission] = !newValue[permission]

        const { success, message } = await OnlineSessionSocketStore.emit( 'meeting:updated-user-permissions', { room_id: meeting_id, newValue, user_id: userDetails.user_id } )

        if ( success ) {
            MeetingStore.updateUsersPermissions( userDetails.user_id, newValue )
        }
        if ( message )
            toast( message )
    }

    return (
        <Box key={userDetails.user_id} bgcolor={Boolean( openOptions ) ? palette.rowHover : ""} padding="10px">


            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box minWidth={0} display="flex" width="100%" alignItems="center" gap="10px">
                    <Avatar sx={{ width: "30px", height: "30px", fontSize: "12px", textTransform: "uppercase", background: USER_CARD_COLOR_SETS[userDetails.color_index || 0].primary, color: palette.common.white }} >{getNameInitials( userDetails?.name )}</Avatar>
                    <Box minWidth={0} flexGrow={1} overflow="hidden">
                        <Tooltip disableInteractive placement='top' title={`${userDetails.name} (${userDetails.email || userDetails.user_id})`}>
                            <Typography fontSize="14px" fontWeight="500" noWrap >{userDetails.name}  {userDetails.reconnecting && <Typography component="span" fontSize="8px" fontWeight="500">offline</Typography>} </Typography>
                        </Tooltip>
                        <Typography fontSize="10px" display="flex" alignItems="center" gap="5px" color='textSecondary' textTransform="capitalize" >
                            {userDetails.type !== "share-client" ? <Typography component="span" color='textSecondary' fontSize="10px" fontWeight={400} borderRadius="2px">{userDetails.meeting_role?.toLowerCase()?.replace( /[^a-zA-Z0-9]/, ' ' )}</Typography> : userDetails.user_id?.includes( MeetingStore.getLocalUser.user_id ) ? "Your presentation" : "Presenting"}
                            <Tooltip disableInteractive title={userDetails.alive_user ? "Alive User" : "External User"}>
                                <Typography sx={{ cursor: "pointer" }} padding="2px" component="span" borderRadius="2px" fontSize="8px" bgcolor={palette.grey[700]}>{userDetails.alive_user ? "AL" : "EX"}</Typography>
                            </Tooltip>
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" gap="10px">
                    {MeetingStore.getCurrentMeeting.meeting_type !== 'hybrid-class' && ( MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id || u.user_id === userDetails.user_id ) ) && userDetails.type !== "share-client" && <IconButton disabled={!MeetingStore.getCurrentMeetingSettings.enable_chats} onClick={() => selectUserForChat( userDetails )} size='small' >
                        <Badge variant='dot' color='error' invisible={!Boolean( MeetingStore.getMeetingChatsDetails.indicators.users?.[userDetails.user_id] )} >
                            {Icons.default.Comment}
                        </Badge>
                    </IconButton>}
                    {MeetingStore.getCurrentMeeting.meeting_type !== 'hybrid-class' && ( MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) ) && userDetails.type !== "share-client" && userDetails.meeting_role !== "meeting-host" && <IconButton onClick={( e ) => setOpenOptions( prev => !prev )} size='small' >
                        {Icons.default.MoreVert}
                    </IconButton>}
                    {userDetails.type === "share-client" && <Typography sx={{ color: `Grey` }} component="span">
                        {Icons.default.PresentToAll}
                    </Typography>}
                </Box>
            </Box>
            <Collapse in={Boolean( openOptions )}  >
                <Box position="relative" display="flex" justifyContent="flex-end" padding="10px" borderRadius="20px" >
                    <Tooltip sx={{ zIndex: 6000 }} title={!userPermission.mic ? "Enable mic" : "Disable Mic"}>
                        <IconButton sx={{ fontSize: "18px" }} onClick={() => updateUserPermissions( 'mic' )}>
                            {userPermission.mic ? Icons.default.Mic : Icons.default.MicOff}
                        </IconButton>
                    </Tooltip>
                    <Tooltip sx={{ zIndex: 6000 }} title={!userPermission.camera ? "Enable Camera" : "Disable Camera"}>
                        <IconButton sx={{ fontSize: "18px" }} onClick={() => updateUserPermissions( 'camera' )}>
                            {userPermission.camera ? Icons.default.VideocamIcon : Icons.default.VideocamOff}
                        </IconButton>
                    </Tooltip>
                    <Tooltip sx={{ zIndex: 6000 }} title={!userPermission.screenSharing ? "Enable Screen Sharing" : "Disable Screen Sharing"}>
                        <IconButton sx={{ fontSize: "18px" }} onClick={() => updateUserPermissions( 'screenSharing' )}>
                            {userPermission.screenSharing ? Icons.default.PresentToAll : Icons.default.CancelPresentation}
                        </IconButton>
                    </Tooltip>
                    <Tooltip sx={{ zIndex: 6000 }} title={userDetails.pinned ? "Unpin User" : "Pin User"}>
                        <IconButton sx={{ fontSize: "18px" }} onClick={() => togglePinUser( userDetails.user_id )}>
                            {!userDetails.pinned ? Icons.default.PushPinOutlined : Icons.default.PushPin}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Collapse>
        </Box>
    )
} )

export default OnlineUsersListCard
